/* ============================================================================
 * canonical v2 promotions-feed interpretation boundary — v2-ONLY (no v1 translation);
 * all feed consumers (shoppable-promotions engine, homepage promotional-slider)
 * MUST read the feed through this module; keep interpretation here, presentation
 * in the consumers.
 * ==========================================================================*/

window.IBPromoFeed = (function () {
  'use strict';

  /* HC - approved copy renders verbatim; dollar figures are no longer stripped - 2026-09-09.
     stripDollars() used to delete every "$12.00" token from title, subtitle and
     description on the way out of normalizeFeed. The September 8 copy revision moved
     most savings phrases from percentages to dollar amounts ("save $27", "save up to
     $16"), and the strip turned them into dangling fragments ("Colorproof kits, save").
     Noah's call, 2026-09-09: show the approved copy as written. Only the short badge
     chip is still tidied, in cleanFraming() over in shoppable-promotions.js. */
  function passthroughCopy(str) {
    if (str === null || str === undefined) return '';
    return String(str).replace(/\s{2,}/g, ' ').trim();
  }

  // resolveSource(promo) — reads the explicit v2 promo.source.{type,handle}.
  // The feed is v2-ONLY (spec §1.3): there is NO websiteLink parsing. A promo
  // with no usable collection/product source becomes a 'link' source pointing at
  // source.fallbackLink, so it is never a dead end.
  function resolveSource(promo) {
    var src = (promo && promo.source) || {};
    var fallback = src.fallbackLink || '';
    if (src.handle && (src.type === 'collection' || src.type === 'product')) {
      return { type: src.type, handle: String(src.handle), fallbackLink: fallback };
    }
    return { type: 'link', kind: 'link', handle: null, fallbackLink: fallback || '#' };
  }
  function sourceHref(source) {
    var src = source || {};
    if (src.type === 'collection' && src.handle) return '/collections/' + src.handle;
    if (src.type === 'product' && src.handle) return '/products/' + src.handle;
    return src.fallbackLink || '';
  }

  // Build a human date label ("June 1 – August 31, 2026") from ISO start/end —
  // used ONLY when a v2 promo omits dates.display.
  var MONTHS = ['January','February','March','April','May','June','July',
    'August','September','October','November','December'];
  function ymdLabel(iso) {
    if (typeof iso !== 'string') return '';
    var m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    var mi = parseInt(m[2], 10) - 1;
    return (mi >= 0 && mi < 12) ? MONTHS[mi] + ' ' + parseInt(m[3], 10) : '';
  }
  function rangeLabel(start, end) {
    var a = ymdLabel(start), b = ymdLabel(end);
    var year = String(end || start || '').slice(0, 4);
    if (a && b) return a + ' – ' + b + (year ? ', ' + year : '');
    if (b) return 'Through ' + b + (year ? ', ' + year : '');
    if (a) return 'From ' + a + (year ? ', ' + year : '');
    return '';
  }

  /* HC - live-stock card gates - 2026-10-26
     A card can follow one product's live stock: stockGate { handle, show }.
     show 'inStock' keeps the card only while that product can be bought;
     'soldOut' keeps it only once it can't. Two cards gated on the same handle
     switch an offer to its fallback the moment the stock runs out, with no feed
     rebuild (the Super Sync 12-tube brush kit, then the mask-only card; Noah,
     2026-10-26). A malformed gate reads as no gate. */
  var STOCK_GATE_SHOWS = { inStock: true, soldOut: true };
  function readStockGate(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = typeof raw.handle === 'string' ? raw.handle.trim() : '';
    if (!handle || STOCK_GATE_SHOWS[raw.show] !== true) return null;
    return { handle: handle, show: raw.show };
  }

  // Normalize a v2 feed into the internal shape the renderer reads. The feed is
  // v2-ONLY (spec §1.3) — legacy/unknown keys are IGNORED. Optional fields are
  // defaulted gently so partial promos never throw.
  function normalizeFeed(json) {
    if (!json) return [];
    var list = json.promotions || (Array.isArray(json) ? json : []);
    if (!Array.isArray(list)) return [];
    return list.map(function (raw, i) {
      var promo = raw || {};
      var d = (promo.dates && typeof promo.dates === 'object') ? promo.dates : {};
      var dates = {
        start: d.start || null,
        end: d.end || null,
        whileSuppliesLast: !!d.whileSuppliesLast,
        display: d.display || rangeLabel(d.start, d.end)
      };
      var st = (promo.status && typeof promo.status === 'object') ? promo.status : {};
      return {
        id: promo.id || ('promo-' + i),
        source: resolveSource(promo),
        brand: promo.brand || '',
        // Stable brand id (tools/promo-feed/brand-key.mjs); '' on feeds built before it.
        brandKey: typeof promo.brandKey === 'string' ? promo.brandKey : '',
        title: passthroughCopy(promo.title || ''),
        subtitle: promo.subtitle ? passthroughCopy(promo.subtitle) : null,
        badge: promo.badge || null,
        discountLabel: promo.discountLabel || null,
        dates: dates,
        tags: Array.isArray(promo.tags) ? promo.tags : [],
        image: promo.image || null,
        description: promo.description ? passthroughCopy(promo.description) : null,
        terms: promo.terms || null,
        included: Array.isArray(promo.included) ? promo.included.filter(function (r) { return r && r.label; }) : [],
        featured: !!promo.featured,
        // HC - promo-level regional gating: compiler-derived region keys pass
        // through untouched; consumers hand them to evaluatePromotionAccess. - 2026-08-01
        restrictedRegions: Array.isArray(promo.restrictedRegions) ? promo.restrictedRegions : [],
        matrixOverridable: !!promo.matrixOverridable,
        // HC - the brand tags a customer needs, derived from the promo's products
        // (enrich-feed-regions.mjs); null on feeds built before it, which the
        // policy then gates by brand name - 2026-09-30
        restrictedBrands: Array.isArray(promo.restrictedBrands) ? promo.restrictedBrands : null,
        sortWeight: typeof promo.sortWeight === 'number' ? promo.sortWeight : 0,
        dateAdded: promo.dateAdded || null,
        stockGate: readStockGate(promo.stockGate),
        status: {
          inStock: st.inStock === undefined ? true : !!st.inStock,
          limitedTime: st.limitedTime === undefined ? (!!dates.end || !!dates.whileSuppliesLast) : !!st.limitedTime,
          // "Limited stock" urgency chip (shoppable-promotions.js urgencyChipHtml).
          limitedStock: !!st.limitedStock
        }
      };
    });
  }

  function parseWindowDate(value, suffix) {
    if (!value) return null;
    var date = new Date(value + suffix);
    var ms = date.getTime();
    return isNaN(ms) ? null : ms;
  }
  function promoWindow(promo) {
    var d = promo && promo.dates;
    if (!d) return { startMs: null, endMs: null };
    return {
      startMs: parseWindowDate(d.start, 'T00:00:00'),
      endMs: d.whileSuppliesLast ? null : parseWindowDate(d.end, 'T23:59:59')
    };
  }

  // Is a promo within its active [start, end] window right now? (Same date parse
  // as urgencyChipHtml: local-time, start at 00:00:00, end at 23:59:59.) Fail
  // open on missing/invalid dates — never hide a promo on a bad or absent date.
  function isPromoLive(promo, nowMs) {
    var activeWindow = promoWindow(promo);
    if (activeWindow.startMs !== null && activeWindow.startMs > nowMs) return false; // not launched yet
    if (activeWindow.endMs !== null && activeWindow.endMs < nowMs) return false; // ended
    return true;
  }

  /* Live stock for gated cards: GET /products/<handle>.js once per handle per page,
     shared by every promo surface on it (this module is a page singleton), and read
     `available`. Unknown stock (HTTP error, 404, bad JSON, no reply within
     STOCK_TIMEOUT_MS) resolves to null, which passesStockGate treats as IN STOCK:
     the inStock card shows and the soldOut card hides, so a shopper never sees both
     cards of a pair, or neither. */
  var STOCK_TIMEOUT_MS = 2500;
  var stockRequests = {};
  function productAvailable(handle, fetchImpl) {
    if (Object.prototype.hasOwnProperty.call(stockRequests, handle)) return stockRequests[handle];
    var doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
    if (!doFetch) return (stockRequests[handle] = Promise.resolve(null));
    var request = Promise.resolve().then(function () {
      return doFetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
    }).then(function (res) {
      if (!res || !res.ok) return null;
      return res.json().then(function (product) {
        return product && typeof product.available === 'boolean' ? product.available : null;
      });
    }).catch(function () { return null; });
    if (typeof setTimeout === 'function') {
      var timer = null;
      var timeout = new Promise(function (resolve) {
        timer = setTimeout(function () { resolve(null); }, STOCK_TIMEOUT_MS);
      });
      request = Promise.race([request.then(function (value) {
        if (typeof clearTimeout === 'function') clearTimeout(timer);
        return value;
      }), timeout]);
    }
    stockRequests[handle] = request;
    return request;
  }

  // availability: { handle: true | false | null }; a missing or null entry is unknown.
  function passesStockGate(promo, availability) {
    var gate = promo && promo.stockGate;
    if (!gate) return true;
    var inStock = !(availability && availability[gate.handle] === false);
    return gate.show === 'soldOut' ? !inStock : inStock;
  }

  // Normalized promos -> Promise of the promos whose stock gate passes right now.
  // Never rejects. Ungated promos pass untouched, and a feed with no gated cards
  // makes no request. Only cards inside their date window (or every card, with
  // opts.includeFuture for "Show Future Promotions") trigger a stock request.
  // opts: { nowMs, includeFuture, fetch }.
  function applyStockGates(promos, opts) {
    opts = opts || {};
    var list = Array.isArray(promos) ? promos : [];
    var nowMs = typeof opts.nowMs === 'number' ? opts.nowMs : Date.now();
    var handles = [];
    list.forEach(function (p) {
      if (!p || !p.stockGate) return;
      if (!opts.includeFuture && !isPromoLive(p, nowMs)) return;
      if (handles.indexOf(p.stockGate.handle) === -1) handles.push(p.stockGate.handle);
    });
    function keep(availability) {
      return list.filter(function (p) { return passesStockGate(p, availability); });
    }
    if (!handles.length) return Promise.resolve(keep({}));
    return Promise.all(handles.map(function (h) { return productAvailable(h, opts.fetch); })).then(function (results) {
      var availability = {};
      handles.forEach(function (h, i) { availability[h] = results[i]; });
      return keep(availability);
    }, function () { return keep({}); });
  }

  return {
    passthroughCopy: passthroughCopy,
    resolveSource: resolveSource,
    sourceHref: sourceHref,
    ymdLabel: ymdLabel,
    rangeLabel: rangeLabel,
    normalizeFeed: normalizeFeed,
    promoWindow: promoWindow,
    isPromoLive: isPromoLive,
    readStockGate: readStockGate,
    passesStockGate: passesStockGate,
    applyStockGates: applyStockGates
  };
})();
